// Agent list — the live left-rail of the dashboard.
//
// Sprint UI-9: rebuilt on shadcn/ui primitives (Button, Input,
// DropdownMenu, SidebarGroup, SidebarGroupLabel). Domain logic
// (archive, rename, delete, fork, context menu, undo toast) is
// preserved from the prior implementation.
//
// Sprint agent-stages — sections are founder-defined workflow stages
// (mockup docs/ui/mockups/02-sidebar-stages.html):
//   * collapsible stage sections (collapsed persists server-side, D2),
//   * filter chips under the search row,
//   * desktop drag & drop between sections via @dnd-kit (mobile moves
//     agents through the long-press menu instead — spec D6),
//   * "Sin estado" inbox + the unchanged "Archivado" section,
//   * pin UI retired (spec D3; recency buckets removed with it).

import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/app.store';
import { useThemeStore } from '../stores/theme.store';
import { getSocket } from '../lib/socket';
import type { Agent, Stage } from '../types';
import { AgentListItem } from './AgentListItem';
import { AgentContextMenu } from './AgentContextMenu';
import { ConfirmDialog } from './ConfirmDialog';
import { ManageStagesDialog } from './ManageStagesDialog';
import { forkAgent, undeleteAgent } from '../lib/api';
import { DEFAULT_EFFORT, DEFAULT_MODEL } from '../lib/models';
import { useSettingsStore } from '../stores/settings.store';
import { useActiveWorkspaceStore } from '../stores/active-workspace.store';
import { useStagesStore } from '../stores/stages.store';
import { isPaletteId, paletteColor } from '../lib/avatar-defaults';
import {
  useGroupedAgents,
  NO_STAGE_KEY,
  ARCHIVED_KEY,
} from '../hooks/useGroupedAgents';
import { useSidebarPagination } from '../hooks/useSidebarPagination';
import { useMediaQuery, MD_QUERY } from '../hooks/useMediaQuery';
import { useUndoToast } from '../hooks/useUndoToast';
import { useToastStore } from '../stores/toast.store';
import { useNotificationsStore } from '../stores/notifications.store';
import { abrirUrlEnLaApp } from '../lib/abrir-en-la-app';
import { formatRelativeTime } from '../lib/time';
import { useTranslation } from '../i18n';
import { RunnersHealthPill } from './RunnersHealthPill';
import { RunnerPickerModal } from './RunnerPickerModal';
import { UsageModal } from './UsageModal';
import { mapSpawnError } from '../lib/spawn-errors';

import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { SidebarGroup } from './ui/sidebar';
import { Icon } from './atoms/Icon';
import { Badge } from './atoms/Badge';

export interface SidebarProps {
  onAgentSelected?: () => void;
  onLogout?: () => void;
}

export function Sidebar({ onAgentSelected, onLogout }: SidebarProps) {
  const { t } = useTranslation();
  const activeWorkspaceId = useActiveWorkspaceStore((s) => s.activeWorkspaceId);
  // Sprint 20.3 PR-10 (D17) — global usage-modal open state lives in
  // the app store so the EC-43 NO_RUNNER_AVAILABLE toast can open it
  // from anywhere ("View runner usage" action). Sidebar still owns the
  // mount; only the boolean moves to the store.
  const usageOpen = useAppStore((s) => s.usageModalOpen);
  const setUsageOpen = useAppStore((s) => s.setUsageModalOpen);
  const grouped = useGroupedAgents({ projectId: activeWorkspaceId });
  const { visibleCount, revealMore } = useSidebarPagination();
  const undoToast = useUndoToast();

  // Sprint agent-stages — stage sections state.
  const stages = useStagesStore((s) => s.stages);
  const toggleStageCollapsed = useStagesStore((s) => s.toggleCollapsed);
  const setAgentStage = useAppStore((s) => s.setAgentStage);
  const isDesktop = useMediaQuery(MD_QUERY);
  // Focus filter (chips). Sidebar-local by design — a filter is a
  // transient lens, not persistent workspace state.
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  // Collapse for the two fixed sections; stage sections persist their
  // collapse server-side (spec D2) via the stages store.
  const [localCollapsed, setLocalCollapsed] = useState<Record<string, boolean>>({});
  const [stagesDialog, setStagesDialog] = useState<{
    open: boolean;
    mode: 'manage' | 'create';
    assignAgentId?: string;
  }>({ open: false, mode: 'manage' });
  const [draggingAgent, setDraggingAgent] = useState<Agent | null>(null);
  // PointerSensor distance keeps plain clicks working — a row only
  // becomes a drag after 6 px of travel (Cursor-baseline motion feel).
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const selectedId = useAppStore((s) => s.selectedAgentId);
  const selectAgent = useAppStore((s) => s.selectAgent);
  const markRead = useAppStore((s) => s.markRead);
  const markUnread = useAppStore((s) => s.markUnread);
  const archiveAgent = useAppStore((s) => s.archiveAgent);
  const renameAgent = useAppStore((s) => s.renameAgent);
  const deleteAgent = useAppStore((s) => s.deleteAgent);
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const openComposer = useAppStore((s) => s.openComposer);
  const createAgentOptimistic = useAppStore((s) => s.createAgentOptimistic);
  // Sprint 11b.3 (D-009 / OAC1) — guard against the user clicking "+" twice
  // in <100 ms. Locally tracked here (not in zustand) because the lock has
  // no rendering implications; the button's `disabled` flag does the
  // visual gating.
  const newAgentLockRef = useRef(false);
  const memoryConstrained = useAppStore((s) => s.concurrency.memoryConstrained);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);
  const pushToast = useToastStore((s) => s.push);

  async function performDelete(agent: Agent): Promise<void> {
    try {
      await deleteAgent(agent.id);
    } catch {
      return;
    }
    pushToast({
      message: t('toast.agentDeleted'),
      action: {
        label: t('toast.undo'),
        onClick: () => {
          upsertAgent(agent);
          void undeleteAgent(agent.id)
            .then(upsertAgent)
            .catch(() => {
              pushToast({
                message: t('toast.undoFailed'),
                durationMs: 8_000,
              });
            });
        },
      },
    });
  }

  const [menu, setMenu] = useState<{
    agent: Agent;
    point: { x: number; y: number } | null;
  } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Agent | null>(null);
  // Sprint runner-picker-on-create — "+" opens the modal first; the
  // actual createAgentOptimistic call fires from onConfirm with the
  // chosen runnerId (undefined = Auto).
  const [pickerOpen, setPickerOpen] = useState(false);

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  function handleSelect(a: Agent) {
    selectAgent(a.id);
    if (a.unread) markRead(a.id);
    onAgentSelected?.();
  }

  function handleNewAgent() {
    // OAC1 — debounce the "+" button at 200 ms. A second click while a
    // create is in flight would open two modals (and on confirm, two
    // backend creates).
    if (newAgentLockRef.current) return;
    newAgentLockRef.current = true;
    setTimeout(() => {
      newAgentLockRef.current = false;
    }, 200);

    // Sprint runner-picker-on-create — "+" now opens the picker first.
    // The actual createAgentOptimistic call fires from onConfirm so the
    // founder can pin a runner (or stay on Auto). The optimistic +
    // error-handling path lives in createWithRunner below — identical
    // to the pre-sprint flow when runnerId === undefined.
    setPickerOpen(true);
  }

  function createWithRunner(runnerId: string | undefined) {
    // Optimistic path: select the temp agent immediately so the composer
    // opens against the placeholder row in <16 ms. The real `POST
    // /api/agents/empty` resolves in the background; the store swaps the
    // row when it lands.
    // Send the canonical model + effort explicitly so the backend never
    // falls back to its own default. Even though the backend default is
    // also canonical post-sprint fix-opus-alias-drift, sending explicit
    // values keeps the chip-vs-CLI alias resolution single-sourced from
    // `web/src/lib/models.ts` and removes any silent path where a CLI
    // alias could enter the system.
    const { tempId, promise } = createAgentOptimistic({
      model: DEFAULT_MODEL,
      effort: DEFAULT_EFFORT,
      permissionMode: useSettingsStore.getState().defaultPermissionMode,
      projectId: useActiveWorkspaceStore.getState().activeWorkspaceId ?? undefined,
      runnerId,
    });
    selectAgent(tempId);
    onAgentSelected?.();

    promise.catch((err: unknown) => {
      // OAC6 — surface the failure inline. The store has already
      // removed the placeholder row + cleared selection.
      // Sprint S20.5 PR-3 (D013-EC-69) — every backend `code` now
      // routes through `mapSpawnError` so the toast copy + intent
      // stay differentiated by branch:
      //   - NO_RUNNER_AVAILABLE → "View runner usage" → opens modal.
      //   - RUNNER_NOT_READY / RUNNER_OFFLINE → "Tap to retry".
      //   - CLAUDE_NOT_AUTHENTICATED → silent (operator must run
      //     `claude /login` on the runner; no UI retry helps).
      //   - unknown code → GENERIC + retry (legacy behaviour).
      const mapping = mapSpawnError(err);
      const message = t(mapping.i18nKey);
      if (mapping.intent === 'usage') {
        pushToast({
          message,
          durationMs: 8_000,
          action: {
            label: t('toast.viewRunnerUsage'),
            onClick: () => setUsageOpen(true),
          },
        });
      } else if (mapping.intent === 'silent') {
        pushToast({ message, durationMs: 10_000 });
      } else {
        pushToast({
          message,
          action: {
            label: t('toast.retry'),
            // Retry stays on the same runner (or Auto) the founder
            // already chose — no need to re-open the picker.
            onClick: () => createWithRunner(runnerId),
          },
        });
      }
      // openComposer() stays as the manual-fallback affordance the user
      // can still trigger from the empty state.
      void openComposer;
    });
  }

  function handleArchive(agent: Agent) {
    void archiveAgent(agent.id, true);
    undoToast({
      message: t('toast.agentArchived'),
      undoAction: () => archiveAgent(agent.id, false),
    });
  }

  function handleRestore(agent: Agent) {
    void archiveAgent(agent.id, false);
    undoToast({
      message: t('toast.agentRestored'),
      undoAction: () => archiveAgent(agent.id, true),
    });
  }

  function handleDeleteArchived(agent: Agent) {
    void performDelete(agent);
  }

  // ── Sprint agent-stages — dnd handlers (desktop only) ──────────────
  function handleDragStart(e: DragStartEvent) {
    const a = useAppStore.getState().agents[String(e.active.id)];
    setDraggingAgent(a ?? null);
  }

  function handleDragEnd(e: DragEndEvent) {
    setDraggingAgent(null);
    if (e.over == null) return;
    const overKey = String(e.over.id);
    const agentId = String(e.active.id);
    const agent = useAppStore.getState().agents[agentId];
    if (!agent) return;
    const nextStageId = overKey === NO_STAGE_KEY ? null : overKey;
    if ((agent.stageId ?? null) === nextStageId) return;
    void setAgentStage(agentId, nextStageId);
  }

  function renderAgentRow(a: Agent, opts: { archived: boolean; stage: Stage | null }) {
    const stageColorHex =
      opts.stage && isPaletteId(opts.stage.color)
        ? paletteColor(opts.stage.color).bg
        : null;
    const item = (
      <AgentListItem
        agent={a}
        selected={selectedId === a.id}
        renaming={renamingId === a.id}
        archived={opts.archived}
        stageColorHex={stageColorHex}
        onSelect={() => handleSelect(a)}
        onContextMenu={(p) => setMenu({ agent: a, point: p })}
        onRenameSubmit={(name) => {
          void renameAgent(a.id, name);
          setRenamingId(null);
        }}
        onRenameCancel={() => setRenamingId(null)}
        onRenameStart={() => setRenamingId(a.id)}
        onArchive={() => handleArchive(a)}
        onRestore={() => handleRestore(a)}
        onDelete={() => {
          if (a.archived) {
            handleDeleteArchived(a);
          } else {
            setConfirmDelete(a);
          }
        }}
      />
    );
    if (opts.archived) return <div key={a.id}>{item}</div>;
    return (
      <DraggableAgentRow key={a.id} agentId={a.id} disabled={!isDesktop || renamingId === a.id}>
        {item}
      </DraggableAgentRow>
    );
  }

  const isSearching = searchQuery.trim().length > 0;
  const hasAnyAgents =
    grouped.sections.some((sec) => sec.agents.length > 0) ||
    grouped.archived.length > 0;
  const visibleSections = grouped.sections.filter(
    (sec) => stageFilter === null || sec.key === stageFilter,
  );

  return (
    <aside
      className="flex flex-col h-full w-full bg-background border-r border-border overflow-hidden"
      data-testid="sidebar"
    >
      {/* Header — Sprint UI compaction (2026-05-11):
          - The redundant "Agents" label was replaced by `RunnersHealthPill`
            (read-only summary of runner capacity + click → UsageModal).
          - The vestigial `ServerSelector` (post-D-013 it had no function
            because the dashboard is centralised on one control plane)
            was deleted along with its row.
          - The Usage 📊 button was deleted on 2026-05-11 (same-day
            follow-up): it opened the exact same UsageModal as the
            adjacent `RunnersHealthPill`, so the two affordances were
            duplicates sitting next to each other. The pill stays as
            the single entry point — Santi flagged the duplication
            within hours of the previous PR landing in prod. The
            EC-43 NO_RUNNER_AVAILABLE toast still opens the modal via
            its inline "View runner usage" action (see `handleNewAgent`),
            which is the only other entry path that mattered. */}
      <header
        className="h-12 px-3 border-b border-border flex items-center justify-between gap-2 shrink-0 pt-safe-top box-content"
        data-testid="sidebar-header"
      >
        <RunnersHealthPill />
        <div className="flex items-center gap-1">
          <NotificationsButton />
          <OfficeButton />
          <SettingsButton />
          <AvatarMenu onLogout={onLogout} />
        </div>
      </header>

      {/* Search + New Agent */}
      <div className="px-3 pt-2 pb-2 border-b border-border flex items-center gap-2">
        <Input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('sidebar.search')}
          className="h-8 bg-secondary border-border text-sm"
          data-testid="agent-search-input"
        />
        <Button
          variant="default"
          size="icon"
          onClick={handleNewAgent}
          disabled={memoryConstrained}
          data-testid="sidebar-new-agent-button"
          aria-label={t('sidebar.newAgent')}
          title={
            memoryConstrained
              ? t('sidebar.newAgentDisabled')
              : t('tooltip.newAgent')
          }
          className="shrink-0 h-8 w-8 bg-action hover:bg-action/90 text-action-foreground"
        >
          <Icon name="Plus" size={16} aria-hidden="true" />
        </Button>
      </div>

      {/* Sprint agent-stages — filter chips (focus one stage; tap again
          or "Todos" to clear). Hidden while no stages exist. */}
      {stages.length > 0 && (
        <div
          className="px-3 pt-2 pb-1 flex items-center gap-1.5 overflow-x-auto shrink-0"
          data-testid="stage-filter-chips"
        >
          <FilterChip
            active={stageFilter === null}
            label={t('stages.filterAll')}
            onClick={() => setStageFilter(null)}
            testid="stage-chip-all"
          />
          {stages.map((s) => (
            <FilterChip
              key={s.id}
              active={stageFilter === s.id}
              label={s.name}
              dotHex={isPaletteId(s.color) ? paletteColor(s.color).bg : undefined}
              onClick={() => setStageFilter((cur) => (cur === s.id ? null : s.id))}
              testid={`stage-chip-${s.id}`}
            />
          ))}
        </div>
      )}

      {/* Agent list */}
      <div className="flex-1 overflow-y-auto" data-testid="agent-list">
        {!hasAnyAgents ? (
          isSearching ? (
            <div
              className="px-4 py-8 text-body text-muted-foreground leading-relaxed"
              data-testid="agent-list-no-results"
            >
              <p>{t('common.searchNoMatch', { query: searchQuery.trim() })}</p>
            </div>
          ) : (
            <div
              className="px-4 py-8 text-body text-muted-foreground leading-relaxed"
              data-testid="agent-list-empty"
            >
              <p className="mb-1 font-550">{t('sidebar.empty.title')}</p>
              <p className="text-caption text-muted-foreground/70">
                {t('sidebar.empty.subtitle')}
              </p>
            </div>
          )
        ) : (
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggingAgent(null)}
          >
            <div className="py-1">
              {visibleSections.map((sec) => (
                <SectionBlock
                  key={sec.key}
                  sectionKey={sec.key}
                  label={sec.stage ? sec.stage.name : t('stages.noStage')}
                  dotHex={
                    sec.stage && isPaletteId(sec.stage.color)
                      ? paletteColor(sec.stage.color).bg
                      : undefined
                  }
                  hollowDot={!sec.stage}
                  agents={sec.agents}
                  collapsed={
                    sec.stage ? sec.stage.collapsed : !!localCollapsed[NO_STAGE_KEY]
                  }
                  onToggleCollapsed={() =>
                    sec.stage
                      ? toggleStageCollapsed(sec.stage.id)
                      : setLocalCollapsed((c) => ({
                          ...c,
                          [NO_STAGE_KEY]: !c[NO_STAGE_KEY],
                        }))
                  }
                  droppable={isDesktop}
                  showWhenEmpty={!isSearching}
                  showSignals
                  visibleLimit={visibleCount(sec.key)}
                  showMoreLabel={(count) => t('sidebar.showMore', { count })}
                  onRevealMore={() => revealMore(sec.key)}
                  renderRow={(a) => renderAgentRow(a, { archived: false, stage: sec.stage })}
                />
              ))}
              {stageFilter === null && (
                <SectionBlock
                  key={ARCHIVED_KEY}
                  sectionKey={ARCHIVED_KEY}
                  label={t('sidebar.section.archived')}
                  agents={grouped.archived}
                  collapsed={!!localCollapsed[ARCHIVED_KEY]}
                  onToggleCollapsed={() =>
                    setLocalCollapsed((c) => ({
                      ...c,
                      [ARCHIVED_KEY]: !c[ARCHIVED_KEY],
                    }))
                  }
                  droppable={false}
                  showWhenEmpty={false}
                  showSignals={false}
                  visibleLimit={visibleCount(ARCHIVED_KEY)}
                  showMoreLabel={(count) => t('sidebar.showMore', { count })}
                  onRevealMore={() => revealMore(ARCHIVED_KEY)}
                  renderRow={(a) => renderAgentRow(a, { archived: true, stage: null })}
                />
              )}
            </div>
            <DragOverlay dropAnimation={null}>
              {draggingAgent ? (
                <DragCard agent={draggingAgent} stages={stages} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {menu ? (
        <AgentContextMenu
          agent={menu.agent}
          position={menu.point}
          onClose={() => setMenu(null)}
          onMarkRead={() => markRead(menu.agent.id)}
          onMarkUnread={() => void markUnread(menu.agent.id)}
          onSetStage={(stageId) => void setAgentStage(menu.agent.id, stageId)}
          onNewStage={() =>
            setStagesDialog({ open: true, mode: 'create', assignAgentId: menu.agent.id })
          }
          onManageStages={() => setStagesDialog({ open: true, mode: 'manage' })}
          onArchive={() => void archiveAgent(menu.agent.id, true)}
          onRestore={() => void archiveAgent(menu.agent.id, false)}
          onRename={() => setRenamingId(menu.agent.id)}
          onDelete={() => {
            if (menu.agent.archived) {
              void performDelete(menu.agent);
            } else {
              setConfirmDelete(menu.agent);
            }
          }}
          onFork={() => {
            void forkAgent(menu.agent.id).catch(() => {});
          }}
        />
      ) : null}

      <ManageStagesDialog
        open={stagesDialog.open}
        mode={stagesDialog.mode}
        onOpenChange={(open) => setStagesDialog((d) => ({ ...d, open }))}
        onCreated={(stage) => {
          if (stagesDialog.assignAgentId) {
            void setAgentStage(stagesDialog.assignAgentId, stage.id);
          }
        }}
      />

      <UsageModal open={usageOpen} onOpenChange={setUsageOpen} />

      <RunnerPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onConfirm={(runnerId) => createWithRunner(runnerId)}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title={t('confirm.deleteAgentSidebar.title')}
        body={
          confirmDelete
            ? t('confirm.deleteAgentSidebar.bodyTemplate', { name: confirmDelete.name })
            : undefined
        }
        confirmLabel={t('confirm.deleteAgentSidebar.confirm')}
        destructive
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          setConfirmDelete(null);
          if (target) void performDelete(target);
        }}
      />
    </aside>
  );
}

// ── Sprint agent-stages — section + dnd building blocks ─────────────────

// One collapsible sidebar section (a stage, "Sin estado", or "Archivado").
// A component (not a render helper) because useDroppable is a hook and
// each section needs its own droppable registration.
function SectionBlock({
  sectionKey,
  label,
  dotHex,
  hollowDot,
  agents,
  collapsed,
  onToggleCollapsed,
  droppable,
  showWhenEmpty,
  showSignals,
  visibleLimit,
  showMoreLabel,
  onRevealMore,
  renderRow,
}: {
  sectionKey: string;
  label: string;
  dotHex?: string;
  hollowDot?: boolean;
  agents: Agent[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  droppable: boolean;
  showWhenEmpty: boolean;
  // Sprint stage-header-signals — attention signals on the header:
  // relative time of the newest agent + unread dot. Off for "Archivado"
  // (archive semantics: never asks for attention — spec D3).
  showSignals: boolean;
  visibleLimit: number;
  showMoreLabel: (count: number) => string;
  onRevealMore: () => void;
  renderRow: (agent: Agent) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: sectionKey,
    disabled: !droppable,
  });

  if (!showWhenEmpty && agents.length === 0) return null;

  const visible = agents.slice(0, visibleLimit);
  const hiddenCount = agents.length - visible.length;
  // Sprint stage-header-signals (D1/D2) — agents arrive sorted by
  // lastActivityAt DESC from useGroupedAgents, so the newest is [0].
  // Derived at render; the Sidebar's 30 s tick keeps the label fresh.
  const newest = showSignals ? agents[0] : undefined;
  // Sprint stage-header-dot-collapsed-only — the header dot only carries
  // signal while the rows (and their per-agent dots) are hidden; expanded,
  // it duplicated the row dots and read as noise (Santi, chat 2026-07-18).
  // The relative time stays in both states: ambient info, not attention.
  const hasUnread = showSignals && collapsed && agents.some((a) => a.unread);

  return (
    <div
      ref={setNodeRef}
      className={[
        'transition-colors duration-motion-instant ease-cursor-out rounded-md',
        // Drop-target highlight — subtle accent wash, no layout shift.
        isOver ? 'bg-accent/40' : '',
      ].join(' ')}
      data-testid={`section-${sectionKey}`}
      data-drop-active={isOver ? 'true' : 'false'}
    >
      <SidebarGroup className="p-0 py-1">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          // Visible header stays 24 px (baseline parity); the ::after
          // overlay expands the tap target toward 44 px (mobile-first
          // HARD rule) without any visual change.
          className="relative w-full flex items-center gap-1.5 px-3 h-6 text-label uppercase tracking-wide text-muted-foreground/70 hover:text-muted-foreground transition-colors duration-motion-instant after:absolute after:content-[''] after:-inset-y-2.5 after:inset-x-0"
          data-testid={`section-toggle-${sectionKey}`}
        >
          <Icon
            name={collapsed ? 'ChevronRight' : 'ChevronDown'}
            size={12}
            aria-hidden="true"
            className="shrink-0"
          />
          {(dotHex || hollowDot) && (
            <span
              className={[
                'inline-block h-2 w-2 rounded-full shrink-0',
                hollowDot ? 'border border-muted-foreground/60' : '',
              ].join(' ')}
              style={dotHex ? { backgroundColor: dotHex } : undefined}
              aria-hidden="true"
            />
          )}
          <span className="truncate">{label}</span>
          {/* eslint-disable-next-line i18n-enforce/no-literal-strings-in-jsx -- decorative count */}
          <span className="ml-1 text-muted-foreground/45">{agents.length}</span>
          {/* Sprint stage-header-signals — right-aligned attention pair:
              unread dot (same Badge atom as rows) + newest-agent relative
              time (same typography role as row timestamps). Rendered
              collapsed or expanded so a folded section still answers
              "¿tengo que mirar aquí?". */}
          {(hasUnread || newest) && (
            <span className="ml-auto flex items-center gap-1.5 shrink-0">
              {hasUnread && (
                <Badge
                  variant="dot"
                  color="info"
                  aria-label={t('agentList.unread')}
                  data-testid={`section-unread-${sectionKey}`}
                />
              )}
              {newest && (
                <span
                  className="text-caption text-muted-foreground/70 normal-case tracking-normal"
                  data-testid={`section-time-${sectionKey}`}
                >
                  {formatRelativeTime(newest.lastActivityAt)}
                </span>
              )}
            </span>
          )}
        </button>
        {!collapsed && (
          <>
            {visible.map((a) => renderRow(a))}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={onRevealMore}
                className="w-full px-3 py-1.5 text-caption text-muted-foreground hover:text-foreground hover:bg-accent/40 text-left transition-colors"
                style={{ minHeight: 'auto', minWidth: 'auto' }}
                data-testid={`show-more-${sectionKey}`}
              >
                {showMoreLabel(hiddenCount)}
              </button>
            )}
          </>
        )}
      </SidebarGroup>
    </div>
  );
}

// Drag source wrapper. PointerSensor's 6 px activation distance keeps
// plain clicks (select agent) working; `touch-action` stays default so
// mobile scrolling is untouched (drag is desktop-only — spec D6).
function DraggableAgentRow({
  agentId,
  disabled,
  children,
}: {
  agentId: string;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: agentId,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role={undefined}
      tabIndex={-1}
      className={isDragging ? 'opacity-40' : undefined}
      data-testid={`draggable-agent-${agentId}`}
    >
      {children}
    </div>
  );
}

// Floating card rendered in the DragOverlay while a row is being dragged.
function DragCard({ agent, stages }: { agent: Agent; stages: Stage[] }) {
  const stage = stages.find((s) => s.id === agent.stageId);
  const hex = stage && isPaletteId(stage.color) ? paletteColor(stage.color).bg : null;
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-popover px-3 py-2 shadow-lg cursor-grabbing">
      <span
        className="w-0.5 self-stretch rounded-full shrink-0"
        style={hex ? { backgroundColor: hex } : undefined}
        aria-hidden="true"
      />
      <span className="truncate text-body text-foreground">{agent.name}</span>
    </div>
  );
}

// Stage filter chip (D6). Visible height stays compact; ::after expands
// the tap target like the section headers. Tap-target math (mobile-first
// HARD ≥44 px): text-caption line 16px + py-1 (8px × 2) + border (1px × 2)
// = 26px visible, plus after:-inset-y-2.5 (10px × 2) = 46px.
function FilterChip({
  active,
  label,
  dotHex,
  onClick,
  testid,
}: {
  active: boolean;
  label: string;
  dotHex?: string;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'relative shrink-0 flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-450 transition-colors duration-motion-instant ease-cursor-out',
        "after:absolute after:content-[''] after:-inset-y-2.5 after:inset-x-0",
        active
          ? 'bg-popover text-foreground border-input'
          : 'border-border text-muted-foreground hover:text-foreground',
      ].join(' ')}
      data-testid={testid}
    >
      {dotHex && (
        <span
          className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
          style={{ backgroundColor: dotHex }}
          aria-hidden="true"
        />
      )}
      <span className="truncate max-w-36">{label}</span>
    </button>
  );
}

// ── Sidebar header trio — rebuilt on shadcn primitives ──────────────────

// Sprint notification-center (spec D5) — the bell is now the persistent
// feed. The dot lights for either signal: per-agent unread turns (the
// pre-existing behaviour) OR unread feed rows. The dropdown lists the
// feed newest-first; tapping a row marks it read and opens the agent.
const TIER_ICON: Record<'A' | 'B' | 'C', 'ShieldAlert' | 'CircleCheck' | 'AlertTriangle'> = {
  A: 'ShieldAlert',
  B: 'CircleCheck',
  C: 'AlertTriangle',
};

function NotificationsButton() {
  const { t } = useTranslation();
  const selectAgent = useAppStore((s) => s.selectAgent);
  const unreadEventCount = useAppStore((s) => {
    let n = 0;
    for (const id in s.agents) {
      if (s.agents[id]?.unread) n += 1;
    }
    return n;
  });
  const notifications = useNotificationsStore((s) => s.notifications);
  const feedUnread = useNotificationsStore((s) => s.unreadCount);
  const hydrate = useNotificationsStore((s) => s.hydrate);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const showDot = unreadEventCount > 0 || feedUnread > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          // The visible button stays 32px (locked baseline parity with the
          // header trio); the ::after overlay expands the tap target to
          // 44×44 without any visual change (mobile-first HARD rule).
          className="relative h-8 w-8 text-muted-foreground hover:text-foreground after:absolute after:-inset-1.5 after:content-['']"
          aria-label={t('topbar.notifications')}
          data-testid="topbar-notifications-button"
        >
          <Icon name="Bell" size={16} aria-hidden="true" />
          {showDot && (
            <span
              className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-status-error ring-2 ring-background"
              aria-hidden="true"
              data-testid="topbar-notifications-dot"
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="topbar-notifications-panel">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-caption text-muted-foreground uppercase">
            {t('notifications.title')}
          </span>
          {feedUnread > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                markAllRead();
              }}
              className="text-caption text-muted-foreground hover:text-foreground py-2 px-2 -my-2 -mx-2"
              data-testid="notifications-mark-all-read"
            >
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="px-3 py-3 text-caption text-muted-foreground" data-testid="notifications-empty">
            {unreadEventCount > 0
              ? t('topbar.unreadAgents', { count: unreadEventCount })
              : t('topbar.noNotifications')}
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto" data-testid="notifications-feed">
            {notifications.map((n) => (
              <DropdownMenuItem
                key={n.id}
                className="flex items-start gap-2.5 px-3 py-2.5 min-h-touch-target cursor-pointer"
                data-testid="notification-row"
                data-notification-id={n.id}
                onSelect={() => {
                  markRead(n.id);
                  // 04-11 — los avisos de Santi no tienen agente: llevan su dirección (oficina?santi=…), que se abre
                  // con la misma navegación que tocar la notificación (lib/abrir-en-la-app.ts).
                  if (n.agentId) selectAgent(n.agentId);
                  else if (n.url) abrirUrlEnLaApp(n.url);
                }}
              >
                <Icon
                  name={TIER_ICON[n.tier]}
                  size={16}
                  className={`shrink-0 mt-0.5 ${
                    n.tier === 'A'
                      ? 'text-status-warning'
                      : n.tier === 'C'
                        ? 'text-status-error'
                        : 'text-muted-foreground'
                  }`}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-body leading-snug ${
                      n.read ? 'text-muted-foreground' : 'text-foreground'
                    }`}
                  >
                    {n.title}
                  </div>
                  {n.body && (
                    <div className="text-caption text-muted-foreground truncate mt-0.5">
                      {n.body}
                    </div>
                  )}
                  <div className="text-caption text-muted-foreground/70 mt-0.5">
                    {formatRelativeTime(n.createdAt)}
                  </div>
                </div>
                {!n.read && (
                  <span
                    className="block h-2 w-2 rounded-full bg-accent shrink-0 mt-1.5"
                    aria-hidden="true"
                    data-testid="notification-unread-dot"
                  />
                )}
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Oficina Virtual F1 (spec 001 T12) — entry point to the /office screen.
// Mirrors SettingsButton so the header trio pattern stays uniform.
function OfficeButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => navigate('/oficina')}
      aria-label={t('office.navLabel')}
      className="h-8 w-8 text-muted-foreground hover:text-foreground"
      data-testid="topbar-office-button"
    >
      <Icon name="Building2" size={16} aria-hidden="true" />
    </Button>
  );
}

function SettingsButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => navigate('/settings')}
      aria-label={t('topbar.settings')}
      className="h-8 w-8 text-muted-foreground hover:text-foreground"
      data-testid="topbar-settings-button"
    >
      <Icon name="Cog" size={16} aria-hidden="true" />
    </Button>
  );
}

function AvatarMenu({ onLogout }: { onLogout?: () => void }) {
  const { t } = useTranslation();
  const connection = useAppStore((s) => s.connectionStatus);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggle);

  const connectionLabel =
    connection === 'connected'
      ? t('connection.connected')
      : connection === 'reconnecting'
        ? t('connection.reconnecting')
        : t('connection.disconnected');

  const dotColor: 'success' | 'warning' | 'error' =
    connection === 'connected'
      ? 'success'
      : connection === 'reconnecting'
        ? 'warning'
        : 'error';

  const handleReconnect = () => {
    if (connection !== 'disconnected') return;
    try {
      getSocket().connect();
    } catch {
      /* socket may not exist in test envs */
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('topbar.accountMenu')}
          className="h-7 w-7 rounded-full bg-secondary text-secondary-foreground font-550 hover:bg-accent hover:text-accent-foreground"
          data-testid="topbar-avatar-button"
        >
          {/* eslint-disable-next-line i18n-enforce/no-literal-strings-in-jsx -- Single-user product, avatar initial */}
          <span className="text-caption">S</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="topbar-avatar-menu">
        <DropdownMenuItem
          onClick={handleReconnect}
          data-testid="topbar-connection-item"
          data-status={connection}
        >
          <Badge variant="dot" color={dotColor} className="w-2.5 h-2.5" />
          <span>{connectionLabel}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={toggleTheme}
          data-testid="topbar-theme-toggle"
          data-theme={theme}
        >
          <Icon name={theme === 'dark' ? 'Moon' : 'Sun'} size={16} aria-hidden="true" />
          <span>{t('topbar.theme')}</span>
        </DropdownMenuItem>
        {onLogout && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLogout}
              data-testid="logout-button"
            >
              <Icon name="LogOut" size={16} aria-hidden="true" />
              <span>{t('topbar.signOut')}</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
